import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import enGbJson from "./translations/en-gb.json";
import zhHkJson from "./translations/zh-hk.json";
import zhCnJson from "./translations/zh-cn.json";

// Local fallback resources
const localResources = {
  en: { ...enGbJson },
  "zh-hk": { ...zhHkJson },
  "zh-cn": { ...zhCnJson },
};

// Cache for remote translations
const translationCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Load remote translations from config
async function loadRemoteTranslations() {
  try {
    console.log(
      "[i18n] Fetching config from:",
      "https://d392t2kbzfe6yu.cloudfront.net/config/config.json"
    );
    const configRes = await fetch(
      "https://d392t2kbzfe6yu.cloudfront.net/config/config.json",
      { cache: "no-store", mode: "cors" }
    );
    console.log("[i18n] Config response status:", configRes.status);
    if (!configRes.ok)
      throw new Error(`Config fetch failed: ${configRes.status}`);

    const config = await configRes.json();
    console.log("[i18n] Config data:", config);
    const localizationUrls = config?.web?.localization;
    console.log("[i18n] Localization URLs:", localizationUrls);

    if (!localizationUrls) {
      console.warn("[i18n] No remote localization URLs found in config");
      return localResources;
    }

    const remoteResources = { ...localResources };

    // Load each language from remote URLs
    const loadPromises = [
      { key: "en", url: localizationUrls.zhENRemoteUrl },
      { key: "zh-hk", url: localizationUrls.zhHKRemoteUrl },
      { key: "zh-cn", url: localizationUrls.zhCNRemoteUrl },
    ].map(async ({ key, url }) => {
      console.log(`[i18n] Loading ${key} from:`, url);
      if (!url) {
        console.warn(`[i18n] No URL for ${key}`);
        return;
      }

      try {
        const cached = translationCache.get(url);
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
          console.log(`[i18n] Using cached translations for ${key}`);
          remoteResources[key] = cached.data;
          return;
        }

        console.log(`[i18n] Fetching fresh translations for ${key}`);
        const res = await fetch(url, { cache: "no-store", mode: "cors" });
        console.log(`[i18n] ${key} response status:`, res.status);
        if (!res.ok) throw new Error(`Translation fetch failed: ${res.status}`);

        const data = await res.json();
        console.log(`[i18n] ${key} translation data:`, data);
        translationCache.set(url, { data, timestamp: Date.now() });
        remoteResources[key] = data;
        console.log(`[i18n] Loaded remote translations for ${key}`);
      } catch (error) {
        console.warn(
          `[i18n] Failed to load remote translations for ${key}:`,
          error
        );
      }
    });

    await Promise.allSettled(loadPromises);
    console.log("[i18n] Final remote resources:", remoteResources);
    return remoteResources;
  } catch (error) {
    console.warn(
      "[i18n] Failed to load remote config, using local translations:",
      error
    );
    return localResources;
  }
}

// Initialize i18n immediately with local resources
if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources: localResources,
    lng: "en",
    fallbackLng: "en",
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
    keySeparator: ".",
    nsSeparator: ":",
  });
}

// Load remote translations in background and update i18n
loadRemoteTranslations()
  .then((resources) => {
    console.log("[i18n] Remote resources loaded:", resources);
    Object.keys(resources).forEach((lang) => {
      if (resources[lang] && resources[lang].translation) {
        // Add remote translations with overwrite=true to replace local ones
        i18n.addResourceBundle(
          lang,
          "translation",
          resources[lang].translation,
          true,
          true
        );
        console.log(`[i18n] Added remote translations for ${lang}`);

        // Test if the translation was actually added
        const testTranslation = i18n.t("homePage.bannerTitle", { lng: lang });
        console.log(`[i18n] Test translation for ${lang}:`, testTranslation);
      } else {
        console.warn(
          `[i18n] No translation data for ${lang}:`,
          resources[lang]
        );
      }
    });
    console.log("[i18n] Remote translations applied");
  })
  .catch((error) => {
    console.warn("[i18n] Failed to apply remote translations:", error);
  });

export default i18n;
